/** @jsxImportSource @emotion/react */

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { nike } from '@nike/nike-design-tokens';
import { useOktaAuth } from '@okta/okta-react';

import { useDispatchWithProgress } from '../hooks/useDispatchWithProgress';

import { InvitationModal, InvitationUpdateModal, StartInvitationButton } from './invitations';
import { UserModal } from './users';
import UsersHeader from './UsersHeader';
import UserManagementTable from './UserManagementTable';
import UsersInvitationsSearchInput from './UsersInvitationsSearchInput';
import UsersInvitationsSort from './UsersInvitationsSort';
import UsersInvitationsTabs from './UsersInvitationsTabs';
import UserManagementSuccessSnackbar from './UserManagementSuccessSnackbar';

import { usersSelectors } from './users/reducer';
import { usersActions } from './users/actions';

const UserManagementPage = () => {
  const requestRoles = useDispatchWithProgress(usersActions.requestRoles);

  const roles = useSelector(usersSelectors.roles);

  useEffect(() => {
    if (!roles.length) {
      requestRoles();
    }
  }, [roles]);

  const { authState } = useOktaAuth();

  return authState?.isAuthenticated ? (
    <>
      <div
        css={{
          background: nike.podiumCdsColorBgPrimary,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 60px)', // Mimic core .content height, which accounts for primary/secondary nav
          width: '100%',
          '@media (min-width: 769px)': {
            height: 'calc(100vh - 128px)'
          }
        }}
      >
        <div
          css={{
            borderBottom: `1px solid ${nike.podiumCdsColorBorderPrimary}`,
            width: '100%'
          }}
        >
          <UsersHeader />
        </div>
        <div
          css={{
            display: 'flex',
            paddingLeft: '24px',
            paddingRight: '24px',
            width: '100%',
            '@media (min-width: 769px)': {
              paddingTop: '24px',
              paddingBottom: '8px' // .nds-input-container adds 12px of padding: 10px top, 2px bottom
            }
          }}
        >
          <div
            css={{
              display: 'none',
              '@media (min-width: 769px)': {
                display: 'block',
                width: 'calc(100% / 3)'
              }
            }}
          >
            <UsersInvitationsSearchInput />
          </div>
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              '@media (min-width: 769px)': {
                width: 'calc(100% / 3)'
              }
            }}
          >
            <UsersInvitationsTabs />
          </div>
          <div
            css={{
              display: 'none',
              '@media (min-width: 769px)': {
                alignItems: 'flex-end',
                display: 'flex',
                justifyContent: 'flex-end',
                paddingBottom: '5px',
                paddingLeft: '12px',
                width: 'calc(100% / 3)'
              }
            }}
          >
            <UsersInvitationsSort />
          </div>
        </div>
        <div
          css={{
            // eslint-disable-next-line max-len
            height: 'calc(100vh - 60px - 148px - 100px - 60px)', // Stack height - header height - input height - tab height
            padding: '24px',
            width: '100%',
            '@media (min-width: 769px)': {
              height: 'calc(100vh - 128px - 148px - 100px)'
            }
          }}
        >
          <UserManagementTable />
        </div>
        <div
          css={{
            borderTop: `1px solid ${nike.podiumCdsColorBorderPrimary}`,
            display: 'flex',
            height: '99px', // 100px input height
            justifyContent: 'flex-end',
            padding: '19.5px 24px', // Top/Bottom padding: (height - button height) / 2
            width: '100%',
            '@media (min-width: 769px)': {
              display: 'none'
            }
          }}
        >
          <StartInvitationButton />
        </div>
      </div>
      <InvitationModal />
      <InvitationUpdateModal />
      <UserModal />
      <UserManagementSuccessSnackbar />
    </>
  ) : null;
};

export default UserManagementPage;
